import React, { useState } from "react";
import { NavLink as RouterNavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "./ThemeToggle";
import {
  Home,
  LayoutDashboard,
  Building2,
  BarChart3,
  Upload,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  User,
  Globe,
  ClipboardList,
  ClipboardCheck,
  MessageSquare,
} from "lucide-react";

const navigation = [
  { name: "Home", href: "/", icon: Home },
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Organizations", href: "/org-management", icon: Building2 },
  {
    name: "Activity Requests",
    href: "/activity-requests",
    icon: ClipboardList,
  },
  {
    name: "Review Requests",
    href: "/review-requests",
    icon: ClipboardCheck,
  },
  { name: "Feedback", href: "/feedback", icon: MessageSquare },
  { name: "Upload", href: "/upload", icon: Upload },
  { name: "Reports", href: "/reports/home", icon: BarChart3 },
  { name: "Ingestion", href: "/ingestion", icon: Globe },
  { name: "Profile", href: "/profile", icon: User },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  if (!user) return null;

  return (
    <aside
      className={`fixed left-0 top-0 z-50 h-screen transition-all duration-300 ease-in-out border-r border-[var(--border-primary)] bg-[var(--surface-primary)] ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-[var(--border-primary)]">
          {!collapsed ? (
            <RouterNavLink to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-gradient">Lil Admin</span>
            </RouterNavLink>
          ) : (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg mx-auto">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <RouterNavLink
                key={item.name}
                to={item.href}
                exact={item.href === "/"}
                className="nav-link"
                activeClassName="active"
                title={collapsed ? item.name : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span>{item.name}</span>}
              </RouterNavLink>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t border-[var(--border-primary)] space-y-3">
          {/* Theme Toggle */}
          <div
            className={`flex ${
              collapsed ? "justify-center" : "justify-between items-center"
            }`}
          >
            {!collapsed && (
              <span className="text-xs font-medium text-[var(--text-muted)]">
                Theme
              </span>
            )}
            <ThemeToggle />
          </div>

          {/* User Profile */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all hover:bg-[var(--surface-secondary)] ${
                collapsed ? "justify-center" : ""
              }`}
            >
              <img
                src={user?.picture}
                alt="Profile"
                className="w-8 h-8 rounded-full border-2 border-[var(--border-primary)]"
              />
              {!collapsed && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                    {user?.name || "Admin"}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] truncate">
                    {user?.email}
                  </p>
                </div>
              )}
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-[var(--surface-primary)] border border-[var(--border-primary)] rounded-xl shadow-xl overflow-hidden z-50">
                <div className="p-3 border-b border-[var(--border-primary)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">
                    Signed in as
                  </p>
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                    {user?.email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-[var(--primary-500)] hover:bg-[var(--primary-600)] text-white flex items-center justify-center shadow-lg transition-all hover:scale-110"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
